<!--
  Upload a blank. The caps are stated BEFORE a byte is chosen, and a refusal is held against the
  picker rather than handed to a toast that scrolls away.
-->
<h1 mat-dialog-title>Upload a practice document</h1>

<div mat-dialog-content>
    <div class="pdd p-t-10">

        <!-- §14.2 - which section's add icon was clicked, said out loud before anything is
             uploaded. It is the one thing about this document that is already decided. -->
        <p class="pds-response-mode" data-test="upload-response-mode">
            <mat-icon>{{ requiresResponse ? 'assignment_return' : 'attach_email' }}</mat-icon>
            <span>{{ responseModeSentence }}</span>
        </p>

        <!--
          §14.2 - THE WHOLE CONSEQUENCE, HERE, BECAUSE HERE IS WHERE IT IS DECIDED.

          This sentence used to live on a toggle in the settings dialog, and it was shown when that
          toggle was turned off. There is no such toggle any more: a document is an information
          sheet because it was uploaded under that section, and the settings dialog configures
          within the kind rather than offering to change it. So the one moment a practice chooses
          is this one, and it is the moment that owes them the full consequence rather than the
          one-line version above.
        -->
        <div class="pdd__alert pdd__alert--warn" *ngIf="!requiresResponse"
            data-test="information-only-consequence">
            <mat-icon>attach_email</mat-icon>
            <div>{{ informationOnlyConsequence }}</div>
        </div>

        <p class="hint" data-test="upload-caps">
            PDF only, up to {{ choice.maxFileMb }} MB and {{ choice.maxPages }} pages. The file is
            inspected when it is uploaded - that inspection is what decides how patients
            complete it.
        </p>

        <!--
          The house drag/drop picker, configured exactly as `consent-form-settings` configures it.

          `[pasteEnabled]="false"` is MANDATORY, not taste: the component registers a DOCUMENT-level
          paste listener in `ngOnInit`, which inside a dialog would steal paste from the name,
          category and description inputs directly below it.

          `[shouldRefresh]="true"` is load-bearing and counter-intuitive. The drop zone's click
          handler is `!fileObjects.length || allowMultiple`, so with one file held and `shouldRefresh`
          off the zone goes DEAD and the user cannot choose a different PDF. `shouldRefresh` clears
          the component's internal array after it emits; our own `choice` object is the real state,
          so nothing is lost.

          `[showGallery]="false"` because the gallery is an IMAGE gallery. What the user reads back
          is the `pdd__chosen` chip below, which is driven by `choice`.
        -->
        <div class="pdd__picker">
            <app-file-upload class="full-width" [fileObjects]="[]" [allowMultiple]="false"
                [allowedFileTypes]="'application/pdf'" [showGallery]="false" [shouldRefresh]="true"
                [pasteEnabled]="false" title="" (fileUpload)="onFilesPicked($event)"
                hintMessage="Drag a PDF here, or click to choose one">
            </app-file-upload>

            <span class="pdd__chosen" *ngIf="choice.file" data-test="chosen-file">
                <mat-icon>picture_as_pdf</mat-icon>
                <strong>{{ choice.file.name }}</strong>
                <span class="pdd__dim">{{ megabytes(choice.file.size) }} MB</span>
                <span class="pdd__dim" *ngIf="choice.pageCount !== null">
                    {{ choice.pageCount }} page(s)
                </span>
                <button mat-icon-button type="button" aria-label="Remove the chosen file"
                    (click)="clearFile()">
                    <mat-icon>close</mat-icon>
                </button>
            </span>
        </div>

        <!-- Held on screen against the picker, never a toast: it is the instruction for what to do
             next, and it has to survive reading the sentence twice. -->
        <div class="pdd__alert pdd__alert--error" *ngIf="choice.error" data-test="new-file-error">
            <mat-icon>error_outline</mat-icon>
            <div>{{ choice.error }}</div>
        </div>

        <mat-form-field appearance="outline" class="full-width">
            <mat-label>Document name</mat-label>
            <input matInput [(ngModel)]="name" name="newName" maxlength="300" data-test="new-name"
                placeholder="e.g. Surgical admission questionnaire">
            <mat-hint>What your staff will look for in the list.</mat-hint>
        </mat-form-field>

        <!--
          CATEGORY — free text with SUGGESTIONS, and the distinction is the whole point.

          A practice groups documents in its own words, so the field cannot become a picker. What
          it can do is offer what they have already used, so that the second admission form is
          filed under the same word as the first instead of under "Admissions" — free text's real
          failure is not a bad category, it is twelve categories with one document each.

          `matchingCategories` drops the exact match, so the panel closes itself once the box holds
          a category that already exists rather than hovering over the field to repeat it.
        -->
        <mat-form-field appearance="outline" class="full-width m-t-20">
            <mat-label>Category</mat-label>
            <input matInput [(ngModel)]="category" name="newCategory" maxlength="100"
                placeholder="e.g. Admission, Medical aid" data-test="upload-category"
                [matAutocomplete]="categorySuggestions">
            <mat-autocomplete #categorySuggestions="matAutocomplete">
                <mat-option *ngFor="let option of filteredCategories" [value]="option"
                    data-test="category-suggestion">{{ option }}</mat-option>
            </mat-autocomplete>
            <mat-hint>Your own wording. Used to group documents.</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width m-t-20">
            <mat-label>Description (for your staff, not the patient)</mat-label>
            <textarea matInput [(ngModel)]="description" name="newDescription" rows="2"></textarea>
        </mat-form-field>

        <p class="hint">
            How patients are asked for this document is set on the next screen, which opens as soon
            as the upload finishes. Which section it belongs to is settled here and is not asked
            again: a document uploaded into the wrong one is deleted and uploaded into the other.
        </p>
    </div>
</div>

<div mat-dialog-actions class="dialog-actions">
    <button mat-button type="button" (click)="cancel()">Cancel</button>
    <button mat-raised-button color="primary" type="button" data-test="upload-submit"
        [disabled]="isUploading" (click)="upload()">Upload</button>
</div>
